/** confirm_sharpen_score facts key → 确认卡短标签（对齐 Figma `income, spending, cash, +4 more`） */
export const SHARPEN_FACT_DISPLAY_LABELS: Record<string, string> = {
  annual_income: "income",
  monthly_expenses: "spending",
  bank_balance: "cash",
  total_debt: "debt",
  investments: "investments",
  housing_piti_monthly: "housing",
  consumer_debt: "consumer debt",
  employer_match: "employer match",
  income_last_year: "last year's income",
  largest_holding_pct: "concentration",
  investment_return_pct: "investment return",
};

/** Sharpen skill tier 优先序 —— 展示顺序与采集优先级一致 */
const SHARPEN_FACT_PRIORITY: readonly string[] = [
  "housing_piti_monthly",
  "consumer_debt",
  "income_last_year",
  "employer_match",
  "annual_income",
  "monthly_expenses",
  "bank_balance",
  "total_debt",
  "investments",
  "largest_holding_pct",
  "investment_return_pct",
];

export const SHARPEN_CONFIRM_SUMMARY_MAX_VISIBLE = 3;

function isPresentFactValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== "";
}

function labelForFactKey(key: string): string {
  return SHARPEN_FACT_DISPLAY_LABELS[key] ?? key.replace(/_/g, " ");
}

/** 按 skill 优先序排列已采集 facts keys */
export function orderedSharpenFactKeys(
  facts: Record<string, unknown>,
): string[] {
  const present = Object.keys(facts).filter((k) =>
    isPresentFactValue(facts[k]),
  );
  const ranked: string[] = [];
  for (const key of SHARPEN_FACT_PRIORITY) {
    if (present.includes(key)) {
      ranked.push(key);
    }
  }
  for (const key of present) {
    if (!ranked.includes(key)) {
      ranked.push(key);
    }
  }
  return ranked;
}

export interface FormatSharpenConfirmSummaryOptions {
  maxVisible?: number;
  /** 默认 `+{n} more`；组件侧可注入 i18n */
  formatMore?: (remaining: number) => string;
}

/**
 * 确认卡摘要兜底：有 facts 时最多展示 [maxVisible] 个短标签，其余 `+N more`；
 * facts 为空则退回接口下发的 summary。
 */
export function formatSharpenConfirmSummary(
  facts: Record<string, unknown> | null | undefined,
  fallbackSummary: string,
  options?: FormatSharpenConfirmSummaryOptions,
): string {
  const maxVisible = options?.maxVisible ?? SHARPEN_CONFIRM_SUMMARY_MAX_VISIBLE;
  const formatMore = options?.formatMore ?? ((n: number) => `+${n} more`);

  if (!facts) {
    return fallbackSummary.trim();
  }

  const keys = orderedSharpenFactKeys(facts);
  if (keys.length === 0) {
    return fallbackSummary.trim();
  }

  const visible = keys.slice(0, maxVisible).map(labelForFactKey);
  const remaining = keys.length - visible.length;
  if (remaining > 0) {
    return `${visible.join(", ")}, ${formatMore(remaining)}`;
  }
  return visible.join(", ");
}
